
import axios from 'axios'
import { defineComponent, inject, onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { AuthPlugin } from '@/auth'
import { channelTypeLabel } from '@/types/breeding-manager'

interface AdminUser {
  id: string
  email?: string
  name?: string
}

interface LinkedUser {
  userId: string
  email?: string
  name?: string
}

interface AdminBreedingManager {
  id: string
  displayName: string
  channelType: 'keito_nai' | 'keito_gai'
  selfServiceEnabled: boolean
  users: LinkedUser[]
}

export default defineComponent({
  name: 'BreedingManagerAdmin',
  setup() {
    const backendUrl: string | undefined = inject('BackendUrl')
    if (!backendUrl) {
      throw new Error('BackendUrl is not provided')
    }

    const auth: AuthPlugin | undefined = inject('Auth')
    if (!auth) {
      throw new Error('Auth plugin is not provided')
    }

    const isLoading = ref(false)
    const managers = ref<AdminBreedingManager[]>([])
    const users = ref<AdminUser[]>([])
    const selectedUserByManager = reactive<Record<string, string>>({})

    const formatUserLabel = (user: AdminUser | LinkedUser) => {
      const id = 'userId' in user ? user.userId : user.id
      return user.name || user.email || id
    }

    const buildHeaders = async () => {
      const idToken = (await auth.getIdTokenClaims({}))?.__raw
      const impersonationToken = localStorage.getItem('impersonationToken')
      const headers: Record<string, string> = {
        Authorization: `Bearer ${idToken}`,
      }
      if (impersonationToken) {
        headers['X-AIMEAT-Impersonation-Token'] = impersonationToken
      }
      return headers
    }

    const loadUsers = async () => {
      try {
        const headers = await buildHeaders()
        const response = await axios.get(`${backendUrl}/v1/admin/users`, { headers })
        users.value = response.data
        if (users.value.length === 0) {
          ElMessage.warning(
            '紐付け候補のユーザーがありません。対象ユーザーに一度アプリへログインしてもらってください。',
          )
        }
      } catch (error) {
        console.error('Failed to load users:', error)
        ElMessage.error('ユーザー一覧の取得に失敗しました（管理者権限が必要です）')
      }
    }

    const loadManagers = async () => {
      isLoading.value = true
      try {
        const headers = await buildHeaders()
        const response = await axios.get(`${backendUrl}/v1/admin/breeding-managers`, {
          headers,
        })
        managers.value = response.data
      } catch (error) {
        console.error('Failed to load breeding managers:', error)
        ElMessage.error('飼養管理者一覧の取得に失敗しました')
      } finally {
        isLoading.value = false
      }
    }

    const linkUser = async (breedingManagerId: string) => {
      const userId = selectedUserByManager[breedingManagerId]
      if (!userId) {
        return
      }
      const headers = await buildHeaders()
      await axios.post(
        `${backendUrl}/v1/admin/breeding-managers/${breedingManagerId}/users`,
        { userId },
        { headers },
      )
      selectedUserByManager[breedingManagerId] = ''
      await loadManagers()
    }

    const unlinkUser = async (breedingManagerId: string, userId: string) => {
      const headers = await buildHeaders()
      await axios.delete(
        `${backendUrl}/v1/admin/breeding-managers/${breedingManagerId}/users/${userId}`,
        { headers },
      )
      await loadManagers()
    }

    onMounted(async () => {
      await Promise.all([loadUsers(), loadManagers()])
    })

    return {
      channelTypeLabel,
      formatUserLabel,
      isLoading,
      managers,
      users,
      selectedUserByManager,
      loadManagers,
      linkUser,
      unlinkUser,
    }
  },
})
